import type { Board, GridPos, Piece } from "../model/board";
import type { Side } from "../model/battle";
import { environmentTexture, type ENVIRONMENTS } from "../assets";
import { stageX, stageY } from "./stage";

export type IsoLayout = { originX: number; originY: number; tileWidth: number; tileHeight: number };

export const BOARD_LAYOUT: IsoLayout = {
  originX: stageX(1194),
  originY: stageY(276),
  tileWidth: stageX(60),
  tileHeight: stageY(30),
};

export const OPPONENT_LAYOUT: IsoLayout = { ...BOARD_LAYOUT, originX: stageX(402), originY: stageY(306) };

// Each painting can place its boards independently. Offsets use the art's 1536×1024 coordinates.
const BOARD_OFFSETS: Record<typeof ENVIRONMENTS[number], Record<Side, [number, number]>> = {
  "emerald-ruins-backdrop": { player: [0, 0], opponent: [0, 0] },
  "autumn-orchard-backdrop": { player: [-14, -12], opponent: [12, -12] },
  "frozen-cliffs-backdrop": { player: [-24, -18], opponent: [-8, -20] },
  "desert-temple-backdrop": { player: [-18, -20], opponent: [16, -14] },
  "moonlit-grove-backdrop": { player: [-28, -22], opponent: [8, -14] },
};

export function boardLayout(side: Side, level: number): IsoLayout {
  const base = side === "player" ? BOARD_LAYOUT : OPPONENT_LAYOUT;
  const [dx, dy] = BOARD_OFFSETS[environmentTexture(level)][side];
  return { ...base, originX: base.originX + stageX(dx), originY: base.originY + stageY(dy) };
}

export const PIECE_HOVER_HEIGHT = 16;
export const PIECE_IMAGE_OFFSET_Y = -27;

export function pieceMetrics(piece: Pick<Piece, "tier">): { size: number; height: number } {
  const size = (132 + Math.min(piece.tier - 1, 10) * 6) * (piece.tier >= 6 ? 1.23 + Math.min(piece.tier - 6, 4) * 0.04 : 1);
  return { size, height: size * (piece.tier < 3 ? 0.38 : piece.tier < 5 ? 0.62 : 0.84) };
}

export function project(pos: GridPos, layout = BOARD_LAYOUT): { x: number; y: number } {
  return {
    x: layout.originX + (pos.x - pos.y) * layout.tileWidth / 2,
    y: layout.originY + (pos.x + pos.y) * layout.tileHeight / 2,
  };
}

// Invert the diamond projection, then test the actual tile shape at its edge.
export function hitCell(
  screenX: number,
  screenY: number,
  width: number,
  height: number,
  layout = BOARD_LAYOUT,
): GridPos | null {
  const dx = (screenX - layout.originX) / (layout.tileWidth / 2);
  const dy = (screenY - layout.originY) / (layout.tileHeight / 2);
  const center = { x: Math.round((dx + dy) / 2), y: Math.round((dy - dx) / 2) };
  let best: { pos: GridPos; distance: number } | null = null;
  for (let y = center.y - 1; y <= center.y + 1; y += 1) {
    for (let x = center.x - 1; x <= center.x + 1; x += 1) {
      if (x < 0 || y < 0 || x >= width || y >= height) continue;
      const point = project({ x, y }, layout);
      const distance = Math.abs(screenX - point.x) / (layout.tileWidth / 2) +
        Math.abs(screenY - point.y) / (layout.tileHeight / 2);
      if (distance <= 1 && (!best || distance < best.distance)) best = { pos: { x, y }, distance };
    }
  }
  return best?.pos ?? null;
}

// Pieces float above their cells. Resolve visible character taps before the tile plane.
export function hitBoardTarget(screenX: number, screenY: number, board: Board, layout = BOARD_LAYOUT): GridPos | null {
  let best: { pos: GridPos; distance: number } | null = null;
  for (let y = 0; y < board.height; y += 1) {
    for (let x = 0; x < board.width; x += 1) {
      const piece = board.cells[y * board.width + x];
      if (!piece) continue;
      const pos = { x, y };
      const point = project(pos, layout);
      const { height } = pieceMetrics(piece);
      const dx = (screenX - point.x) / (height * 0.48);
      const dy = (screenY - (point.y - PIECE_HOVER_HEIGHT - height / 2)) / (height / 2 + 4);
      const distance = dx * dx + dy * dy;
      if (distance <= 1 && (!best || distance < best.distance)) best = { pos, distance };
    }
  }
  return best?.pos ?? hitCell(screenX, screenY, board.width, board.height, layout);
}
